import React, { useCallback, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';

import PatientExistsForm from './PatientExistsForm';
import { patientExists } from '../../processes/patient';
import { showErrorToast } from '@amigoapp/doca-react';
import { FormatterUtils } from '../../utils';
import { clearMask } from '../../utils/formatter';
import {
	useAnswersStore,
	useCompanyStore,
	usePatientStore
} from '../../stores';

function PatientExistsFormContainer() {
	const history = useHistory();

	const [loading, setLoading] = useState(false);
	const [patientNotFound, setPatientNotFound] = useState(false);

	const { companySettings: { settings } } = useCompanyStore();
	const { setAnswer } = useAnswersStore();
	const { patient, setPatient } = usePatientStore();

	const validate = values => {
		const errors = {};

		if (!values.contact_cellphone) {
			errors.contact_cellphone = 'O telefone é um campo obrigatório.';
		}

		if (patientNotFound) {
			if (!values.born) {
				errors.born = 'A data de nascimento é obrigatória.';
			}

			if (!values.gender) {
				errors.gender = 'O gênero é obrigatório.';
			}
		}

		return errors;
	};

	const handleExistingPatient = useCallback(async cleanPhone => {
		const { data } = await patientExists({ contact_cellphone: cleanPhone });

		if (data) {
			setPatient({
				patient: { contact_cellphone: cleanPhone }
			});
			history.push('/otp-validation');
			return;
		}

		setPatientNotFound(true);
		setPatient({
			patient: { contact_cellphone: cleanPhone }
		});
	}, [setPatient, history]);

	const handleNewPatient = useCallback(values => {
		const cleanPhone = clearMask(values.contact_cellphone);

		setPatient({
			patient: {
				contact_cellphone: cleanPhone,
				born: values.born,
				gender: values.gender
			}
		});

		setAnswer('patient', {
			contact_cellphone: cleanPhone,
			born: values.born,
			gender: values.gender
		});

		history.push('/select-event-group-payment-method');
	}, [setPatient, setAnswer, history]);

	const handleSubmitPress = useCallback(async values => {
		setLoading(true);

		try {
			const cleanPhone = clearMask(values.contact_cellphone);

			if (patientNotFound) {
				handleNewPatient(values);
				return;
			}

			await handleExistingPatient(cleanPhone);
		} catch (error) {
			showErrorToast('Erro inesperado, tente novamente depois.');
		} finally {
			setLoading(false);
		}
	}, [patientNotFound, handleExistingPatient, handleNewPatient]);

	const {
		handleSubmit,
		handleChange,
		isValid,
		values,
		setFieldValue,
		validateForm
	} = useFormik({
		initialValues: {
			contact_cellphone: patient?.contact_cellphone
				? FormatterUtils.formatPhone(patient.contact_cellphone)
				: '',
			born: patient?.born || '',
			gender: patient?.gender || '',
		},
		validate,
		validateOnMount: true,
		enableReinitialize: true,
		onSubmit: handleSubmitPress
	});

	useEffect(() => {
		if (patientNotFound) {
			validateForm();
		}
	}, [patientNotFound, validateForm]);

	const onChangeWithMask = useCallback((event, mask) => {
		if (event.target.value) {
			event.target.value = mask(event.target.value);
		}
		handleChange(event);
	}, [handleChange]);

	const handleNavigateBackPress = useCallback(() => {
		if (patientNotFound) {
			setPatientNotFound(false);
			setFieldValue('born', '');
			setFieldValue('gender', '');
			return;
		}

		history.goBack();
	}, [history, patientNotFound, setFieldValue]);

	return (
		<PatientExistsForm
			values={values}
			loading={loading}
			isValid={isValid}
			variant={settings?.theme?.name}
			patientNotFound={patientNotFound}
			setFieldValue={setFieldValue}
			onChange={handleChange}
			onChangeWithMask={onChangeWithMask}
			onSubmitClick={handleSubmit}
			handleNavigateBackPress={handleNavigateBackPress}
		/>
	);
}

export default PatientExistsFormContainer;
